import { Injectable } from '@angular/core';

/**
 * Shared visibility-based "view re-entered" detection for Medical Inquiry screens.
 * Use when the component may be the root of a child nav (ionViewWillEnter does not fire)
 * or when opened as a full page (ionViewWillEnter fires). Call restoreNow() from
 * ionViewWillEnter and pass the same callback to startObserving; call destroy() in ngOnDestroy.
 *
 * Provide this service in each component's providers so each gets its own instance.
 */
@Injectable()
export class MedicalInquiryFooterRestoreService {
  private intersectionObserver: IntersectionObserver | null = null;
  private hadBeenVisible = false;
  private isVisible = false;
  private onReEnter: (() => void) | null = null;

  /**
   * Start observing the element; when it becomes visible again after having been hidden,
   * the callback is invoked. Call from ngAfterViewInit.
   * @param runOnFirstVisible – when true, callback runs every time the element becomes visible
   *   (use for Detail where the element may never leave the viewport when navigating away).
   */
  startObserving(element: HTMLElement, callback: () => void, runOnFirstVisible = false): void {
    this.onReEnter = callback;
    if (typeof IntersectionObserver === 'undefined' || !element) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          if (!this.isVisible) {
            this.isVisible = true;
            if ((runOnFirstVisible || this.hadBeenVisible) && this.onReEnter) {
              this.onReEnter();
            }
          }
        } else {
          if (this.isVisible) {
            this.isVisible = false;
            this.hadBeenVisible = true;
          }
        }
      },
      { threshold: 0.1 }
    );
    this.intersectionObserver.observe(element);
  }

  /**
   * Invoke the restore callback (e.g. from ionViewWillEnter).
   */
  restoreNow(): void {
    if (this.onReEnter) {
      this.onReEnter();
    }
  }

  /**
   * Disconnect observer and clear callback. Call from ngOnDestroy.
   */
  destroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.onReEnter = null;
  }
}
